'use client'

import type { NotificationView } from '@package/notification'
import { isGuardianNotification } from '@package/notification'
import { Avatar, AvatarFallback, AvatarImage } from '@ui/web/components'

import { NotificationCard } from './notification-card'

interface RecipientNotificationCardProps {
  notification: NotificationView
  onSelect?: () => void
}

/**
 * Every card the reader *receives*, in two shapes the designs treat as one component.
 *
 * With an actor — a recommendation, a new assignment, a grade — a person did this, so the
 * card leads with their avatar and the cover sits under the sentence.
 *
 * Without one, the card is a roll-up or a deadline: "5 siswa belum menyelesaikan tugas",
 * "waktu pengerjaan akan segera berakhir". Nobody sent those, so an avatar would have to
 * invent a sender, and the cover moves to the left of a single line of text.
 *
 * The distinction is `snapshot.actorName`, not `NotificationEvent.actorId`: the snapshot is
 * what the card can actually draw, and a cron-emitted event has neither.
 *
 * One exception, and it is the whole point of the `guardian.*` mirrors: a parent's card is about
 * their child, not about whoever set the work. It leads with the child's avatar so a parent with
 * three children can tell three otherwise identical cards apart at a glance, and so the face in
 * the corner always agrees with the name in the sentence.
 */
export function RecipientNotificationCard({ notification, onSelect }: RecipientNotificationCardProps) {
  const { actorName, actorAvatarUrl, studentName, studentAvatarUrl, organizationName, thumbnailUrl } =
    notification.snapshot

  const isGuardian = isGuardianNotification(notification.type)
  const leadingName = isGuardian ? studentName : actorName
  const leadingAvatarUrl = isGuardian ? studentAvatarUrl : actorAvatarUrl
  // The family's name beside the timestamp: a guardian card belongs to the family context and
  // opens there, whichever app the reader is in. School-side cards carry no label — they belong
  // to the organization the reader is already standing in.
  const contextLabel = isGuardian ? organizationName : undefined

  if (!leadingName) {
    return (
      <NotificationCard
        notification={notification}
        layout={thumbnailUrl ? 'media-left' : 'text-only'}
        contextLabel={contextLabel}
        onSelect={onSelect}
      />
    )
  }

  return (
    <NotificationCard
      notification={notification}
      layout={thumbnailUrl ? 'media-below' : 'text-only'}
      contextLabel={contextLabel}
      onSelect={onSelect}
      leading={
        <Avatar size='sm'>
          <AvatarImage src={leadingAvatarUrl} alt='' />
          <AvatarFallback>{leadingName.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
      }
    />
  )
}
